import React, { useEffect, useState } from 'react';
import { Box, Modal, Checkbox, FormControlLabel, IconButton, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createRole, editRole } from '../../store/role/role.action';
import Button from '../../extra/Button';
import CloseIcon from '@mui/icons-material/Close';
import { permissionError } from '../../../util/Alert';
import { setToast } from '../../../util/toast';

// Recreated based on the image / Sidebar.js modules
// Grouped into relevant sections exactly as shown in Sidebar.js sections
export const permissionModules = [
    {
        section: "PRODUCT MANAGEMENT",
        modules: [
            { name: "Attribute" },
            { name: "Category" },
            { name: "PromoCode" },
            { name: "Product Request" },
            { name: "Product" },
            { name: "Reels" },
        ]
    },
    {
        section: "REPORT MANAGEMENT",
        modules: [
            { name: "Reels Report" },
            { name: "Report Reason" },
        ]
    },
    // {
    //     section: "STAFF MANAGEMENT",
    //     modules: [
    //         { name: "Access Roles" },
    //         { name: "Staff" },
    //     ]
    // },
    {
        section: "ORDER & SELLER",
        modules: [
            { name: "Seller" },
            { name: "Live Seller" },
            { name: "Order" },
        ]
    },
    {
        section: "USER MANAGEMENT",
        modules: [
            { name: "User" },
        ]
    },

    {
        section: "LANGUAGE",
        modules: [
            { name: "App Language" },
        ]
    },

    {
        section: "FINANCE",
        modules: [
            { name: "Seller Withdrawal" },
            { name: "Admin Earning" },
        ]
    },
    {
        section: "HELP & SETTINGS",
        modules: [
            { name: "FAQ" },
            // { name: "Setting" },
        ]
    }
];

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: 800,
    maxHeight: '90vh',
    backgroundColor: 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
};


const allActions = ["List", "Create", "Edit", "Delete"];

const RoleDialogue = ({ page, size }) => {
    const dispatch = useDispatch();
    const { dialogue: open, dialogueData } = useSelector((state) => state.dialogue);
    const hasPermission = useSelector((state) => state.admin.admin.flag);

    const [roleName, setRoleName] = useState("");
    const [permissions, setPermissions] = useState({}); // { ModuleName: ["List", "Create"] }
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (dialogueData) {
            setRoleName(dialogueData.name);
            const permMap = {};
            dialogueData.permissions.forEach(p => {
                permMap[p.module] = p.actions;
            });
            setPermissions(permMap);
        }
    }, [dialogueData]);

    const handleClose = () => {
        dispatch({ type: "CLOSE_DIALOGUE" });
    };

    const handleActionChange = (moduleName, action) => {
        const currentActions = permissions[moduleName] || [];
        let newActions;
        if (action === "All") {
            newActions = currentActions.length === allActions.length ? [] : [...allActions];
        } else {
            if (currentActions.includes(action)) {
                newActions = currentActions.filter(a => a !== action);
            } else {
                newActions = [...currentActions, action];
            }
        }

        setPermissions({
            ...permissions,
            [moduleName]: newActions
        });
        if (errors.permissions) {
            setErrors({ ...errors, permissions: "" });
        }
    };

    const handleSectionGrantAll = (section, checked) => {
        const newPermissions = { ...permissions };
        section.modules.forEach(module => {
            newPermissions[module.name] = checked ? [...allActions] : [];
        });
        setPermissions(newPermissions);
        if (errors.permissions) {
            setErrors({ ...errors, permissions: "" });
        }
    };

    const isSectionAllGranted = (section) => {
        return section.modules.every(module =>
            (permissions[module.name] || []).length === allActions.length
        );
    };

    const handleSubmit = () => {
        if (!hasPermission) return permissionError();
        let tempErrors = {};
        if (!roleName) tempErrors.name = "Role Name is required";

        const formattedPermissions = Object.keys(permissions)
            .filter(m => permissions[m].length > 0)
            .map(m => ({
                module: m,
                actions: permissions[m]
            }));

        if (formattedPermissions.length === 0) {
            tempErrors.permissions = "At least one permission is required";
        }

        if (Object.keys(tempErrors).length > 0) {
            setErrors(tempErrors);
            return;
        }

        if (dialogueData) {
            const hasNameChanged = roleName !== dialogueData.name;

            const permissionsEqual = (p1, p2) => {
                if (p1.length !== p2.length) return false;
                for (let perm of p1) {
                    const match = p2.find(x => x.module === perm.module);
                    if (!match) return false;
                    if (perm.actions.length !== match.actions.length) return false;
                    const sortedA1 = [...perm.actions].sort();
                    const sortedA2 = [...match.actions].sort();
                    if (!sortedA1.every((val, index) => val === sortedA2[index])) return false;
                }
                return true;
            };

            const hasPermissionsChanged = !permissionsEqual(formattedPermissions, dialogueData.permissions);

            if (!hasNameChanged && !hasPermissionsChanged) {
                setToast("info", "No changes made to update.");
                handleClose();
                return;
            }

            const updatePayload = {};
            if (hasNameChanged) updatePayload.name = roleName;
            if (hasPermissionsChanged) updatePayload.permissions = formattedPermissions;

            dispatch(editRole(dialogueData._id, updatePayload)).then(res => {
                if (res.status) {
                    handleClose();
                }
            });
        } else {
            const payload = {
                name: roleName,
                permissions: formattedPermissions
            };

            dispatch(createRole(payload)).then(res => {
                if (res.status) {
                    handleClose();
                }
            });
        }
    };

    const totalModulesAccess = Object.keys(permissions).filter(m => permissions[m].length > 0).length;

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style}>
                <div className="model-header d-flex justify-content-between align-items-center p-3">
                    <div className="headerTitle fw-bold text-white">
                        <h5 className="m-0">{dialogueData ? "Edit Role" : "Create Role"}</h5>
                        <p className="text-muted m-0" style={{ fontSize: '13px' }}>
                            This role has access to {totalModulesAccess} modules with various permission levels.
                        </p>
                    </div>
                    <div
                        className="closeBtn "
                        onClick={handleClose}
                    >
                        <i class="fa-solid fa-xmark"></i>
                    </div>
                </div>

                <div className="model-body p-3" style={{ overflowY: 'auto' }}>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Role Name"
                            variant="outlined"
                            value={roleName}
                            onChange={(e) => {
                                setRoleName(e.target.value);
                                setErrors({});
                            }}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </div>

                    {errors.permissions && (
                        <div className="mb-3">
                            <p className="errorMessage" style={{ margin: 0 }}>{errors.permissions}</p>
                        </div>
                    )}

                    {permissionModules.map((section, sIdx) => (
                        <div key={sIdx} className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
                                <h6 className="m-0 text-uppercase text-muted" style={{ fontSize: '14px', fontWeight: 600 }}>
                                    {section.section}
                                </h6>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isSectionAllGranted(section)}
                                            onChange={(e) => handleSectionGrantAll(section, e.target.checked)}
                                        />
                                    }
                                    label={<span style={{ fontSize: '13px' }}>Grant all in section</span>}
                                    labelPlacement="start"
                                />
                            </div>

                            <div className="card shadow-none border">
                                <div className="table-responsive">
                                    <table className="table permission-table m-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th style={{ width: '35%', verticalAlign: 'middle', paddingLeft: '15px' }}>Module</th>
                                                {["All", ...allActions].map(action => (
                                                    <th key={action} className="text-center" style={{ fontSize: '12px', verticalAlign: 'middle', fontWeight: 600 }}>{action}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {section.modules.map((module, mIdx) => {
                                                const modulePermissions = permissions[module.name] || [];
                                                const isAll = modulePermissions.length === allActions.length;

                                                return (
                                                    <tr key={mIdx}>
                                                        <td style={{ paddingLeft: '15px', verticalAlign: 'middle' }}>
                                                            <div className="d-flex align-items-center">
                                                                <div>
                                                                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>{module.name}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={isAll}
                                                                onChange={() => handleActionChange(module.name, "All")}
                                                                sx={{ '&.Mui-checked': { color: '#ff5c5c' } }}
                                                            />
                                                        </td>
                                                        {allActions.map(action => (
                                                            <td key={action} className="text-center" style={{ verticalAlign: 'middle' }}>
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={modulePermissions.includes(action)}
                                                                    onChange={() => handleActionChange(module.name, action)}
                                                                    sx={{ '&.Mui-checked': { color: '#ff5c5c' } }}
                                                                />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="model-footer">
                    <div className="d-flex justify-content-end align-items-center p-3">
                        <Button
                            btnName={dialogueData ? 'Update' : 'Submit'}
                            onClick={handleSubmit}
                            btnColor={`btnBlackPrime text-light`}
                            style={{ borderRadius: "5px", width: "80px" }}
                            newClass={`me-2`}
                        />
                        <Button
                            btnName={`Close`}
                            btnColor="myCustomButton"
                            onClick={handleClose}
                            style={{ borderRadius: "5px", width: "80px" }}
                        />
                    </div>

                </div>
            </Box>
        </Modal>
    );
};

export default RoleDialogue;
